/**
 * `aiConfig.ts` — AI harness endpoints + gates.
 *
 * Mirrors the safe AI transport fields in Unity's `CloudSettings` (snake-cased
 * JSON in each environment's normal `app-config.json`):
 *
 *   cloud_settings.aisvc_host              → aiSvcHost
 *
 * Host/urls are safe to commit (same policy as `cloudConfig.ts`); secrets are
 * **never** committed here — provide them via `.env.local` when needed:
 *
 *   VITE_AI_ENABLED=true|false                 (seeded by `vite.config.ts`)
 *   VITE_AISVC_HOST=...
 *
 * **One host, one credential.** The browser sends its Cognito ID token to
 * `aisvc_host` for every AI call — Responses, files, vector stores, and the
 * realtime key mint alike. That Campfire proxy validates the user token and
 * replaces it with its server-side OpenAI credential, so no provider key and
 * no shared app key is shipped to a client.
 *
 * This is why there is no mint URL or mint app key here any more: the mint
 * moved onto the same host as everything else (`/v1/realtime/client_secrets`),
 * so it is reached through the same authenticated path rather than a separate
 * service with a separate committed secret.
 */

export interface AiConfig {
  /** Master AI gate — port of `AppConfig.AIEnabled` (`ai_enabled`, default enabled). */
  readonly aiEnabled: boolean;
  /** Realtime + Responses host (`cloud_settings.aisvc_host`). */
  readonly aiSvcHost: string;
}

const DEFAULT_AISVC_HOST = "oaiproxy-dev.campfire3d.com";

function readEnv(name: string): string {
  const value = (import.meta.env as Record<string, unknown>)[name];
  return typeof value === "string" ? value.trim() : "";
}

export function getAiConfig(): AiConfig {
  const enabledRaw = readEnv("VITE_AI_ENABLED").toLowerCase();
  return {
    aiEnabled: enabledRaw === "" || enabledRaw === "true" || enabledRaw === "1",
    aiSvcHost: readEnv("VITE_AISVC_HOST") || DEFAULT_AISVC_HOST,
  };
}

/**
 * Whether `aisvc_host` names a Campfire-operated proxy rather than OpenAI
 * itself. The two are authenticated differently: ours takes the signed-in
 * user's Cognito token and holds the provider credential, OpenAI takes a
 * minted ephemeral key.
 *
 * A locally run oaiproxy is ours in every respect, so it matches too —
 * otherwise local development silently takes the direct-to-OpenAI branch and
 * offers a credential the local proxy does not accept.
 */
export function isCampfireAiProxyHost(host: string): boolean {
  if (host.includes("campfire3d.com")) {
    return true;
  }
  const authority = host.trim().replace(/^https?:\/\//i, "");
  return /^(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(authority);
}

/**
 * Whether a model id can ride the realtime session at all. Measured against
 * the dev proxy (2026-08-13): `/v1/realtime?model=` accepts only the
 * `gpt-realtime*` family and refuses everything else at connect with
 * `invalid_model` ("not supported in realtime mode") — a Responses-family
 * reasoning model (gpt-5.5 / gpt-5.6-sol / gpt-5.6-luna / gpt-5.6-terra)
 * can only be reached over
 * HTTP `/v1/responses`, which is what the orchestrator's text bridge does.
 *
 * Re-measured 2026-08-28, and the earlier note was incomplete rather than
 * wrong: the endpoint also accepts `gpt-realtime-2.1` and
 * `gpt-realtime-2.1-mini`, and — the part that matters downstream — **2.x
 * ACCEPTS `reasoning.effort` where 1.5 refuses it** with `invalid_value`
 * ("Unsupported option for this model"). That refusal is the whole reason
 * `AIOrchestratorModel.handleSessionErrorForModelPolicy` carries an
 * effort-drop self-heal; on the current stock model the effort now reaches
 * the wire and is echoed back in `session.updated`.
 *
 * The predicate itself needs no change for a new version — it is a family
 * prefix test, which is why 2.1 required no edit here.
 */
export function isRealtimeFamilyModel(model: string): boolean {
  return model.trim().toLowerCase().startsWith("gpt-realtime");
}

/** Port of `AIConfigUtility.IsAIEnabledAsync` (config is sync on web). */
export function isAiEnabled(): boolean {
  return getAiConfig().aiEnabled;
}
